import IconButton from '@material-ui/core/IconButton';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CloseIcon from '@material-ui/icons/Close';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import MenuIcon from '@material-ui/icons/Menu';
import clsx from 'clsx';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';

import FilledButton from '@/components/FilledButton.jsx';

import { Context as AuthContext } from '@/context/AuthContext.js';
import { ContextProviderTwo } from '@/context/DoctorsContextTwo.jsx';

import { Auth } from '@/service/amplifyAuth.ts';

import { APP_HEADER_HEIGHT_VAR } from '@/utils/constants.js';

import { resetSearchSelection, useConfigStore, useLoggingStore, usePersistedPrismicStore } from '@/store.js';

import LoginDialogBox from './Common/LoginDialogBox.jsx';

const useStyles = makeStyles((theme) => {
    return {
        wrapper: {
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            background: theme.palette.common.white,
            borderBottom: '1px solid rgba(46, 46, 46, 0.15)',
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            maxWidth: theme.layout.contentWidth,
            padding: '40px 50px 20px 50px',
            width: '100%',
            justifyContent: 'space-between',
            minHeight: '2.5rem',
            position: 'relative',
            [theme.breakpoints.down('xs')]: {
                padding: '20px 20px 20px 20px',
            },
        },
        customerIcon: {
            width: '100%',
            maxWidth: '20rem',
            maxHeight: '8rem',
            [theme.breakpoints.down('xs')]: {
                width: '10rem',
                maxHeight: '6rem',
            },
        },
        headerNav: {
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            position: 'relative',
            userSelect: 'none',
        },
        navLinks: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.5rem',
        },
        // Mobile-only dropdown that drops from the header container and stacks vertically.
        menu: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            padding: '0.75rem 0',
            zIndex: 10,
            border: '1px solid #E0E0E0',
            borderRadius: '0 0 0.5rem 0.5rem',
        },
        menuLink: {
            appearance: 'none',
            border: 'none',
            background: 'none',
            margin: 0,
            fontFamily: 'inherit',
            fontSize: '1rem',
            fontWeight: theme.weight.subhead,
            lineHeight: 1.5,
            outline: 'none',
            textDecoration: 'none',
            color: theme.palette.common.darkGrey,
            cursor: 'pointer',
            padding: '0.25rem 0.5rem',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            borderBottom: `2px solid transparent`,
            transition: 'border-color 0.2s ease',
            '&:hover': {
                borderBottomColor: alpha(theme.palette.common.secondary, 0.4),
            },
            // Keyboard focus: the browser outline is suppressed above, so restore a
            // high-contrast indicator for :focus-visible (keyboard/AT users only).
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.common.darkGrey}`,
                outlineOffset: '2px',
                borderRadius: '2px',
            },
            [theme.breakpoints.down('xs')]: {
                padding: '0.75rem 1.25rem',
                textAlign: 'left',
                borderBottom: 'none',
                borderLeft: `4px solid transparent`,
                '&:hover': {
                    borderLeftColor: alpha(theme.palette.common.secondary, 0.4),
                },
            },
        },
        currentRoute: {
            borderBottom: `2px solid ${theme.palette.common.secondary}`,
            fontWeight: theme.weight.heading,
            // The hover rule on menuLink is class + pseudo-class, so it would otherwise fade this.
            '&:hover': {
                borderBottomColor: theme.palette.common.secondary,
            },
            [theme.breakpoints.down('xs')]: {
                borderBottom: 'none',
                borderLeft: `4px solid ${theme.palette.common.secondary}`,
                '&:hover': {
                    borderLeftColor: theme.palette.common.secondary,
                },
            },
        },
        headerRight: {
            display: 'flex',
            alignItems: 'center',
        },
        hamburgerButton: {
            color: theme.palette.common.darkGrey,
            padding: '0.5rem',
        },
    };
});

const routesWithoutHeader = ['/all-forms-code'];

const linkTestIds = {
    '/': 'nav-dashboard',
    '/appointments': 'nav-appointments',
    '/account': 'nav-account',
    '/health-profile-overview': 'nav-health-profile',
};

const Header = () => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const {
        state: { user },
        setuser,
        setUserData,
    } = useContext(AuthContext);
    const { emptyStates } = useContext(ContextProviderTwo);
    const theme = useTheme();
    const location = useLocation();
    const history = useHistory();
    const headerObserver = useRef(null);
    const [openLogin, setOpenLogin] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const {
        [i18n.language]: { contentHeading: prismicData, dataSet },
    } = usePersistedPrismicStore((state) => state.headerData);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);

    // Tracks the sticky header's height so scroll anchors can offset by it. The logo loads after
    // mount and resizes the header, so a one-off measurement would go stale.
    const measureHeader = (element) => {
        headerObserver.current?.disconnect();

        if (!element) {
            document.documentElement.style.removeProperty(APP_HEADER_HEIGHT_VAR);
            return;
        }

        const publishHeight = () => {
            const { height } = element.getBoundingClientRect();
            document.documentElement.style.setProperty(APP_HEADER_HEIGHT_VAR, `${height}px`);
        };

        publishHeight();
        headerObserver.current = new ResizeObserver(publishHeight);
        headerObserver.current.observe(element);
    };

    const logout = async (e) => {
        e.preventDefault();
        await Auth.signOut();
        setuser(null);
        emptyStates();
        setUserData(null);
        history.replace('/');
        window.location.reload();
    };

    useEffect(() => {
        if (!prismicData || !dataSet) return;
        useLoggingStore.setState({
            largeText: prismicData?.error_fallback_large_text[0]?.text,
            smallText: prismicData?.error_fallback_small_text[0]?.text,
            ctaText: prismicData?.error_fallback_cta[0]?.text,
        });
    }, [prismicData, dataSet]);

    if (routesWithoutHeader.includes(location.pathname)) return null;

    if (!dataSet) {
        return (
            <div className={classes.wrapper} ref={measureHeader}>
                <div className={classes.container} />
            </div>
        );
    }

    const loginButton = (
        <FilledButton onPress={() => setOpenLogin(true)} text={prismicData.login[0].text} dataTestId="nav-login" />
    );

    const desktopAuth = !user ? (
        loginButton
    ) : (
        <div className={classes.headerNav}>
            <nav className={classes.navLinks}>
                {currentCustomer.header_links.map((headerLink) => (
                    <Link
                        key={headerLink.link + headerLink.link_label[0].text}
                        className={clsx(
                            classes.menuLink,
                            location.pathname === headerLink.link && classes.currentRoute
                        )}
                        to={headerLink.link}
                        data-testid={linkTestIds[headerLink.link]}
                    >
                        {headerLink.link_label[0].text}
                    </Link>
                ))}
                <button
                    className={classes.menuLink}
                    onClick={logout}
                    data-testid="nav-logout"
                    type="button"
                    aria-label={t('Logout')}
                    title={t('Logout')}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <ExitToAppIcon style={{ fontSize: '1.375rem' }} />
                </button>
            </nav>
        </div>
    );

    const hamburger = (
        <IconButton
            className={classes.hamburgerButton}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? t('CloseMenu') : t('OpenMenu')}
            aria-expanded={mobileMenuOpen}
            data-testid="nav-hamburger"
        >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
    );

    let headerRightContent;
    if (!isMobile) {
        headerRightContent = desktopAuth;
    } else if (!user) {
        // Logged-out on mobile: show the single login option directly, no hamburger.
        headerRightContent = loginButton;
    } else {
        headerRightContent = hamburger;
    }

    return (
        <div className={classes.wrapper} ref={measureHeader}>
            <div className={classes.container}>
                <Link to="/" onClick={resetSearchSelection}>
                    <img
                        className={classes.customerIcon}
                        src={currentCustomer.customer_icon_large.url}
                        alt={currentCustomer.customer_name[0].text}
                    />
                </Link>
                <div className={classes.headerRight}>{headerRightContent}</div>

                {isMobile && mobileMenuOpen && user && (
                    <div className={classes.menu}>
                        {currentCustomer.header_links.map((headerLink) => (
                            <Link
                                key={headerLink.link + headerLink.link_label[0].text}
                                className={clsx(
                                    classes.menuLink,
                                    location.pathname === headerLink.link && classes.currentRoute
                                )}
                                to={headerLink.link}
                                onClick={() => setMobileMenuOpen(false)}
                                data-testid={linkTestIds[headerLink.link]}
                            >
                                {headerLink.link_label[0].text}
                            </Link>
                        ))}
                        <button
                            className={classes.menuLink}
                            onClick={(e) => {
                                setMobileMenuOpen(false);
                                logout(e);
                            }}
                            data-testid="nav-logout"
                            type="button"
                        >
                            {t('Logout')}
                        </button>
                    </div>
                )}

                <LoginDialogBox openPopUp={openLogin} onClosePopUp={() => setOpenLogin(false)} />
            </div>
        </div>
    );
};

export default Header;
